import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HardDrive, Users } from 'lucide-react';
import {
  fetchSpeicherJeKonto,
  fetchStorageStats,
  runStorageCleanup,
  formatBytes,
} from '@/api/admin.api';
import { Auslastung, SpeicherDiagramm } from '@/components/SpeicherDiagramm';

const CATEGORY_LABELS: Record<string, string> = {
  photo: 'Fotos', document: 'Dokumente', certificate: 'Urkunden',
  letter: 'Briefe', artifact: 'Artefakte', map: 'Karten',
  newspaper: 'Zeitungen', churchbook: 'Kirchenbücher',
  audio: 'Audio', video: 'Video', other: 'Sonstiges',
};

export function AdminStoragePage() {
  const queryClient = useQueryClient();
  const [confirmCleanup, setConfirmCleanup] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'storage'],
    queryFn: fetchStorageStats,
    staleTime: 30_000,
  });

  // Getrennte Abfrage: die Belegung je Konto liest die Datenbank, die
  // Statistik oben zusätzlich die Platte. Zusammengelegt würde die schnellere
  // auf die langsamere warten.
  const { data: konten, isLoading: kontenLaden } = useQuery({
    queryKey: ['admin', 'storage', 'konten'],
    queryFn: fetchSpeicherJeKonto,
    staleTime: 30_000,
  });

  const cleanupMutation = useMutation({
    mutationFn: runStorageCleanup,
    onSuccess: (result) => {
      setConfirmCleanup(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'storage'] });
      toast.success(
        `Bereinigung abgeschlossen: ${result.deletedFiles} Dateien gelöscht (${formatBytes(result.freedBytes)} freigegeben), ${result.removedRecords} Datenbankeinträge entfernt.`,
      );
    },
    onError: (err: Error) => toast.error(`Fehler: ${err.message}`),
  });

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Speicherverwaltung</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-5xl space-y-6">
          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          )}
          {isError && (
            <p className="text-center text-sm text-red-500">Fehler beim Laden der Speicherstatistiken.</p>
          )}

          {data && (
            <>
              {/* Übersicht */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Gesamt</p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">{formatBytes(data.totalBytes)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Dateien</p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">{data.fileCount}</p>
                </div>
                <div className={`rounded-lg border p-4 shadow-sm ${
                  data.orphanedFiles.length > 0 || data.ghostRecords.length > 0
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-green-200 bg-green-50'
                }`}>
                  <p className={`text-xs font-medium uppercase tracking-wider ${
                    data.orphanedFiles.length > 0 || data.ghostRecords.length > 0 ? 'text-amber-600' : 'text-green-600'
                  }`}>Probleme</p>
                  <p className={`mt-1 text-2xl font-bold ${
                    data.orphanedFiles.length > 0 || data.ghostRecords.length > 0 ? 'text-amber-800' : 'text-green-800'
                  }`}>
                    {data.orphanedFiles.length + data.ghostRecords.length}
                  </p>
                </div>
              </div>

              {/* Nach Kategorie */}
              {data.byCategory.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <h2 className="font-semibold text-slate-900">Speicher nach Kategorie</h2>
                  </div>

                  {/* Erst das Bild, dann die Zahlen. Die Tabelle darunter
                      bleibt: sie ist die Fassung, die sich vorlesen und
                      kopieren lässt – und die einzige, die auch dann noch
                      stimmt, wenn jemand Farben nicht unterscheidet. */}
                  <div className="border-b border-slate-100 p-4">
                    <SpeicherDiagramm
                      teile={data.byCategory.map((c: { category: string; count: number; bytes: number }) => ({
                        kategorie: c.category,
                        bytes: c.bytes,
                        dateien: c.count,
                      }))}
                      nichtZugeordnet={
                        data.totalBytes -
                        data.byCategory.reduce(
                          (s: number, c: { bytes: number }) => s + c.bytes,
                          0,
                        )
                      }
                    />
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-2 font-medium">Kategorie</th>
                        <th className="px-4 py-2 font-medium text-right">Dateien</th>
                        <th className="px-4 py-2 font-medium text-right">Größe</th>
                        <th className="px-4 py-2 font-medium">Anteil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byCategory.map((cat: { category: string; count: number; bytes: number }) => (
                        <tr key={cat.category} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-2 font-medium text-slate-800">
                            {CATEGORY_LABELS[cat.category] ?? cat.category}
                          </td>
                          <td className="px-4 py-2 text-right text-slate-600">{cat.count}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{formatBytes(cat.bytes)}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                                <div
                                  className="h-full rounded-full bg-blue-500"
                                  style={{ width: `${data.totalBytes > 0 ? (cat.bytes / data.totalBytes) * 100 : 0}%` }}
                                />
                              </div>
                              <span className="w-10 text-right text-xs text-slate-500">
                                {data.totalBytes > 0 ? ((cat.bytes / data.totalBytes) * 100).toFixed(0) : 0}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Wer belegt den Platz?
                  Die Frage, die man stellt, wenn die Platte voll wird – und
                  die diese Seite bisher nicht beantworten konnte. Gerechnet
                  wird je KONTO, so wie das Kontingent gilt: was ein Mitglied
                  hochlädt, geht auf den Platz des Besitzers. */}
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                  <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                    <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    Belegung je Konto
                  </h2>
                  {konten && (
                    <p className="text-xs text-slate-500">
                      {konten.gesamt.konten} Konten
                      {konten.gesamt.knapp > 0 && (
                        <span className="ml-2 text-amber-700">{konten.gesamt.knapp} über 80 %</span>
                      )}
                      {konten.gesamt.voll > 0 && (
                        <span className="ml-2 font-medium text-red-600">
                          {konten.gesamt.voll} über der Grenze
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {kontenLaden && (
                  <div className="flex justify-center py-8">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                  </div>
                )}

                {konten && (
                  <>
                    {/* Ab `sm` eine Tabelle, darunter Karten: sechs Spalten
                        auf einem Telefon werden entweder unlesbar klein oder
                        schieben die Seite seitwärts. */}
                    <div className="hidden overflow-x-auto sm:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                            <th className="px-4 py-2 font-medium">Konto</th>
                            <th className="px-4 py-2 font-medium">Tarif</th>
                            <th className="px-4 py-2 text-right font-medium">Belegt</th>
                            <th className="px-4 py-2 font-medium">Auslastung</th>
                            <th className="px-4 py-2 text-right font-medium">Bäume</th>
                            <th className="px-4 py-2 text-right font-medium">Dateien</th>
                          </tr>
                        </thead>
                        <tbody>
                          {konten.konten.map((k) => (
                            <tr key={k.userId} className="border-b border-slate-50 last:border-0">
                              <td className="max-w-[16rem] truncate px-4 py-2 font-medium text-slate-800">
                                {k.email}
                              </td>
                              <td className="px-4 py-2 text-slate-500">{k.tarif}</td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                {formatBytes(k.belegtBytes)}
                              </td>
                              <td className="px-4 py-2">
                                <Auslastung prozent={k.prozent} />
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                {k.baeume}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                {k.dateien}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <ul className="divide-y divide-slate-100 sm:hidden">
                      {konten.konten.map((k) => (
                        <li key={k.userId} className="px-4 py-3">
                          <p className="truncate font-medium text-slate-800">{k.email}</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {k.tarif} · {k.baeume} {k.baeume === 1 ? 'Stammbaum' : 'Stammbäume'} ·{' '}
                            {k.dateien} {k.dateien === 1 ? 'Datei' : 'Dateien'}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-3">
                            <span className="text-sm tabular-nums text-slate-700">
                              {formatBytes(k.belegtBytes)}
                              {k.grenzeBytes !== null && (
                                <span className="text-slate-400"> / {formatBytes(k.grenzeBytes)}</span>
                              )}
                            </span>
                            <Auslastung prozent={k.prozent} />
                          </div>
                        </li>
                      ))}
                    </ul>

                    {konten.ohneBesitzer.belegtBytes > 0 && (
                      <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <HardDrive className="mr-1 inline h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                        Dazu {formatBytes(konten.ohneBesitzer.belegtBytes)} in{' '}
                        {konten.ohneBesitzer.baeume}{' '}
                        {konten.ohneBesitzer.baeume === 1 ? 'Stammbaum' : 'Stammbäumen'} ohne
                        Besitzer – sie zählen zu keinem Kontingent und tauchen oben nicht auf.
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Probleme */}
              {(data.orphanedFiles.length > 0 || data.ghostRecords.length > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h2 className="mb-2 font-semibold text-amber-900">⚠️ Bereinigung empfohlen</h2>
                  <div className="space-y-1 text-sm text-amber-800">
                    {data.orphanedFiles.length > 0 && (
                      <p>
                        <strong>{data.orphanedFiles.length}</strong> verwaiste Datei(en) auf der Festplatte
                        ({formatBytes(data.orphanedFiles.reduce((s: number, f: { bytes: number }) => s + f.bytes, 0))}) – nicht in der Datenbank.
                      </p>
                    )}
                    {data.ghostRecords.length > 0 && (
                      <p>
                        <strong>{data.ghostRecords.length}</strong> Geistereintrag/-einträge in der Datenbank –
                        Datei(en) nicht auf der Festplatte.
                      </p>
                    )}
                  </div>

                  {!confirmCleanup ? (
                    <button
                      onClick={() => setConfirmCleanup(true)}
                      className="mt-3 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
                    >
                      Bereinigung starten
                    </button>
                  ) : (
                    <div className="mt-3 flex items-center gap-3">
                      <p className="text-sm font-medium text-amber-900">Wirklich bereinigen?</p>
                      <button
                        onClick={() => cleanupMutation.mutate()}
                        disabled={cleanupMutation.isPending}
                        className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {cleanupMutation.isPending ? 'Bereinige…' : 'Ja, bereinigen'}
                      </button>
                      <button
                        onClick={() => setConfirmCleanup(false)}
                        className="rounded-md border border-amber-300 px-4 py-2 text-sm text-amber-800 hover:bg-amber-100"
                      >
                        Abbrechen
                      </button>
                    </div>
                  )}
                </div>
              )}

              {data.orphanedFiles.length === 0 && data.ghostRecords.length === 0 && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                  ✓ Keine Probleme gefunden – Speicher ist konsistent.
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminStoragePage;
